import React, { useEffect } from 'react';
import { ActivityIndicator } from 'react-native';
import Button from 'src/components/10-atoms/button/Button';
import AttachmentThumbnailList from 'src/components/10-atoms/form/inputs/FileInput/thumbnail/AttachmentThumbnailList';
import DeleteIcon from 'src/components/10-atoms/icons/DeleteIcon';
import FeatherIcon from 'src/components/10-atoms/icons/FeatherIcon';
import Column from 'src/components/10-atoms/layout/Column';
import PageMeta from 'src/components/10-atoms/seo/PageMeta';
import ConfirmDeleteDrawer from 'src/components/20-helper-views/bottom-drawer/common-drawers/ConfirmDelete';
import { bottomDrawerHelperActions } from 'src/components/20-helper-views/bottom-drawer/useBottomDrawer';
import { overlayHelperViewActions } from 'src/components/20-helper-views/overlay/useOverlay';
import Page from 'src/components/templates/page/Page.template';
import PageNavbar from 'src/components/templates/page/PageNavbar.template';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import usePlaylistIntro from 'src/lib/playlist/hooks/usePlaylistIntro';
import useIsSuperAdmin from 'src/modules/authentication/hooks/useIsSuperAdmin';
import useI18n from 'src/modules/translations/hooks/useI18n';
import styleUtils from 'src/utils/styles';
import EPBreadCrumbs from '../../components/EPBreadCrumbs';
import QuestionList from '../../containers/QuestionList';
import { NodeType } from '../../graphql/common/types/node';
import QPFormOverlay from 'src/modules/exam-preparation/screens/Subject/container/QPTab/QPForm';
import getHeirarchyJsonLD from '../../seo/getHeirarchyJsonLD';
import { pageAnalytics } from './analytics';
import QPContributor from './components/QPContributor';
import EFFormOverlay from './containers/EFForm';
import useData from './useData';
const QP = ({ navigation, route }) => {
    const { examLevel, subject, qp } = route.params;
    const [{ theme }] = useTheme();
    const { translate } = useI18n();
    const [state, actions] = useData();
    useEffect(() => {
        if (!qp) {
            actions.qpDetailsQuery.fetch();
        }
    }, []);
    const ancestorsDetails = state.qpDetailsQuery.data?.ep_qp.ancestorsDetails;
    const communityDetails = ancestorsDetails?.community;
    const examLevelDetails = examLevel || ancestorsDetails?.examLevel;
    const subjectDetails = subject ||
        ancestorsDetails?.writtenSubject ||
        ancestorsDetails?.oralsSubject;
    const qpDetails = state.qpDetailsQuery.data?.ep_qp || qp;
    const breadCrumbsLoading = !subject && state.qpDetailsQuery.loading;
    const { trigger: triggerPlaylistIntro } = usePlaylistIntro({
        onConfirm() {
            pageAnalytics.setWidget('TTS_Intro').setTarget('Get_Started').click();
        },
    });
    const handleSpeakPress = useStableCallback(async () => {
        await triggerPlaylistIntro();
        actions.playlist.play();
    });
    const handleEFUploadPress = useStableCallback(async () => {
        if (!qpDetails?.id) {
            return;
        }
        overlayHelperViewActions.show({
            component: (React.createElement(EFFormOverlay, { qpID: qpDetails.id, onClose: overlayHelperViewActions.hide, onComplete: overlayHelperViewActions.hide })),
        });
    });
    const efUploadEnabled = qpDetails?.type === NodeType.WRITTEN_QP && qpDetails?.config?.WRITTEN_QUESTION?.hasExaminersFeedback;
    const isSuperAdmin = useIsSuperAdmin();
    const handleEditPress = useStableCallback(() => {
        if (!qpDetails?.id || !isSuperAdmin) {
            return;
        }
        overlayHelperViewActions.show({
            component: (React.createElement(QPFormOverlay, { qpID: qpDetails.id, subjectID: subjectDetails?.id, onClose: overlayHelperViewActions.hide, onComplete: () => {
                    overlayHelperViewActions.hide();
                    actions.qpDetailsQuery.fetch();
                } })),
        });
    });
    const handleDeletePress = useStableCallback(() => {
        if (!qpDetails?.id || !isSuperAdmin) {
            return;
        }
        bottomDrawerHelperActions.show({
            component: (React.createElement(ConfirmDeleteDrawer, { title: translate('Delete Question Paper'), msg: translate('Are you sure you want to delete this question paper? This action cannot be undone.'), deleteLoading: state.deleteQPMutation.loading, onCancel: bottomDrawerHelperActions.hide, onDelete: async () => {
                    await actions.deleteQPMutation.delete({
                        variables: {
                            qpID: qpDetails.id,
                        },
                    });
                    bottomDrawerHelperActions.hide();
                    navigation.goBack();
                } })),
        });
    });
    return (React.createElement(Page, null,
        qpDetails && subjectDetails && communityDetails && examLevelDetails && (React.createElement(PageMeta, { title: `Question Paper: ${qpDetails.data.label}`, description: `Official Question paper for the ${communityDetails.data.title} / ${examLevelDetails.data.title} / ${subjectDetails.data.title} Exam held on ${qpDetails.data.label}`, canonical: `exam-prep/qp/${qpDetails.id}`, jsonLd: {
                '@context': 'https://schema.org',
                '@graph': [
                    {
                        '@type': 'ScholarlyArticle',
                        name: `Marine Engineering ${communityDetails.data.title} / ${examLevelDetails.data.title} / ${subjectDetails.data.title} / ${qpDetails.data.label} Question Paper`,
                        description: `Official Question paper for the ${communityDetails.data.title} / ${examLevelDetails.data.title} ${subjectDetails.data.title} Exam held on ${qpDetails.data.label}`,
                        learningResourceType: 'Exam',
                        educationalLevel: 'Advanced / Professional',
                        about: {
                            '@type': 'Thing',
                            name: 'Marine Engineering COC Exams',
                        },
                        provider: {
                            '@type': 'Organization',
                            name: 'MarineSamraj',
                            url: 'https://marinesamraj.com',
                        },
                        url: `https://marinesamraj.com/exam-prep/qp/${qpDetails.id}`,
                    },
                    getHeirarchyJsonLD(ancestorsDetails, {
                        examLevel: examLevelDetails,
                        subject: subjectDetails,
                        qp: qpDetails,
                    }),
                ].filter(Boolean),
            } })),
        React.createElement(Column, { bbw: 1 },
            React.createElement(PageNavbar, { backType: "back", title: translate('Question Paper'), onBackPress: navigation.goBack, actions: [
                    qpDetails?.type === NodeType.WRITTEN_QP && {
                        Icon(props) {
                            return state.playlist.playlistQuery.loading ? (React.createElement(ActivityIndicator, { size: "small" })) : (React.createElement(FeatherIcon, { name: "play-circle", ...props }));
                        },
                        title: 'Speak',
                        onPress() {
                            pageAnalytics.setWidget('Navbar').setTarget('TTS_Play').click();
                            handleSpeakPress();
                        },
                    },
                    isSuperAdmin && !!qpDetails?.id && {
                        Icon: props => React.createElement(FeatherIcon, { name: "edit", ...props }),
                        title: translate('Edit'),
                        onPress: handleEditPress
                    },
                    isSuperAdmin && !!qpDetails?.id && {
                        Icon: DeleteIcon,
                        title: translate('Delete'),
                        onPress: handleDeletePress
                    },
                ] }),
            React.createElement(EPBreadCrumbs, { loading: breadCrumbsLoading, visibleCount: qpDetails?.type === NodeType.ORALS_QP ? 3 : 2, ancestorsDetails: ancestorsDetails, knownNodes: {
                    examLevel,
                    subject: subjectDetails,
                    qp: qpDetails,
                } })),
        React.createElement(QuestionList, { ListHeaderComponent: () => (React.createElement(Column, null,
                state.qpAuthorQuery.data?.ep_qp.author && qpDetails ? (React.createElement(QPContributor, { author: state.qpAuthorQuery.data.ep_qp.author, ctime: qpDetails.ctime })) : null,
                isSuperAdmin && qpDetails?.data.qpImages?.length && (React.createElement(AttachmentThumbnailList, { bg: theme.palette.background.dark, data: qpDetails?.data.qpImages })))), style: styleUtils.containerHeight, contentContainerStyle: styleUtils.floatingBtnGutter, variant: "question-paper", queryState: state.listQuery, queryActions: actions.listQuery, variables: state.listQueryVar, onQuestionPress: actions.trackQuestionPress, getQuestionTo: actions.getQuestionDetailsTo }),
        efUploadEnabled && (React.createElement(Button, { variant: 'float', LeftIcon: props => React.createElement(FeatherIcon, { name: "plus", ...props }), onPress: handleEFUploadPress }, "Examiners Feedback"))));
};
export default QP;
